import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import {
  Award, Download, Sparkles, Lightbulb, User, DollarSign, Palette,
  Megaphone, Users, Mic, Briefcase, Zap, FileCheck, Lock,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PLACEHOLDER = "✏️ Still building this section!";

function Section({ icon, title, complete, children, testId }) {
  return (
    <div
      className={`rounded-xl p-4 border-2 ${complete ? "bg-white border-purple-200" : "bg-gray-50 border-dashed border-gray-300"}`}
      data-testid={testId}
    >
      <div className="flex items-center gap-2 mb-2">
        <span className="text-purple-600">{icon}</span>
        <h4 className="font-bold text-gray-800 text-sm uppercase tracking-wide">{title}</h4>
      </div>
      <div className={complete ? "text-gray-800" : "text-gray-500 italic text-sm"}>
        {complete ? children : PLACEHOLDER}
      </div>
    </div>
  );
}

function KV({ label, value }) {
  if (!value) return null;
  return (
    <div className="text-sm">
      <span className="font-semibold text-gray-700">{label}:</span>{" "}
      <span className="text-gray-800">{value}</span>
    </div>
  );
}

function PublicJourneyMap() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // Inject Open Graph + Twitter Card meta tags client-side once data loads.
  // Crawlers that execute JS (and our internal SSR proxy at /api/public/journey-map/{token}/preview.html)
  // will see these tags. Cleans them up on unmount so they don't leak into other routes.
  useEffect(() => {
    if (!data) return;
    const s = data.sections || {};
    const profile = s.entrepreneur_profile;
    const businessName = (s.business_name || "").trim() || "My Mini Business Plan";
    const firstName = data.student?.name || "Student";
    const profileText = profile?.name ? ` · ${profile.icon || ""} ${profile.name}` : "";
    const title = `${businessName} · Mini Business Plan v1`;
    const desc = `By ${firstName}${profileText}. A youth-built business plan from the Lemonade Stand Business Plan — standards-aligned with Nevada CTE / Perkins V + CCTC.`;
    const imageUrl = `${API}/public/journey-map/${token}/og-image.png`;
    const pageUrl = `${window.location.origin}/journey/${token}`;

    const tags = [
      { kind: "name", key: "description", value: desc },
      { kind: "property", key: "og:type", value: "website" },
      { kind: "property", key: "og:site_name", value: "Lemonade Stand Business Plan" },
      { kind: "property", key: "og:title", value: title },
      { kind: "property", key: "og:description", value: desc },
      { kind: "property", key: "og:image", value: imageUrl },
      { kind: "property", key: "og:image:width", value: "1200" },
      { kind: "property", key: "og:image:height", value: "630" },
      { kind: "property", key: "og:image:alt", value: `${title} — Mini Business Plan share card` },
      { kind: "property", key: "og:url", value: pageUrl },
      { kind: "name", key: "twitter:card", value: "summary_large_image" },
      { kind: "name", key: "twitter:title", value: title },
      { kind: "name", key: "twitter:description", value: desc },
      { kind: "name", key: "twitter:image", value: imageUrl },
      { kind: "name", key: "twitter:image:alt", value: `${title} — Mini Business Plan share card` },
    ];

    const created = [];
    const oldTitle = document.title;
    document.title = title;

    tags.forEach(({ kind, key, value }) => {
      const sel = `meta[${kind}="${key}"]`;
      let el = document.head.querySelector(sel);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(kind, key);
        document.head.appendChild(el);
        created.push(el);
      } else {
        // Stash original to restore on cleanup
        el.setAttribute("data-jm-prev", el.getAttribute("content") || "");
      }
      el.setAttribute("content", value);
    });

    return () => {
      document.title = oldTitle;
      // Remove tags we created
      created.forEach((el) => {
        if (el.parentNode) el.parentNode.removeChild(el);
      });
      // Restore tags we modified
      tags.forEach(({ kind, key }) => {
        const sel = `meta[${kind}="${key}"]`;
        const el = document.head.querySelector(sel);
        if (el && el.hasAttribute("data-jm-prev")) {
          el.setAttribute("content", el.getAttribute("data-jm-prev"));
          el.removeAttribute("data-jm-prev");
        }
      });
    };
  }, [data, token]);

  const load = async () => {
    try {
      const res = await fetch(`${API}/public/journey-map/${token}`);
      if (res.status === 404) {
        setError("This share link does not exist.");
        return;
      }
      if (res.status === 410) {
        try {
          const j = await res.json();
          setError(j.detail || "This share link is no longer available.");
        } catch (e) {
          setError("This share link is no longer available.");
        }
        return;
      }
      if (!res.ok) {
        setError("Unable to load this Mini Business Plan.");
        return;
      }
      const json = await res.json();
      setData(json);
    } catch (e) {
      setError("Network error. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const downloadPDF = async () => {
    setDownloading(true);
    try {
      const res = await fetch(`${API}/public/journey-map/${token}/pdf`);
      if (!res.ok) return;
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `mini_business_plan_${token.slice(0, 8)}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => window.URL.revokeObjectURL(url), 30000);
    } catch (e) { /* ignore */ }
    finally { setDownloading(false); }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-orange-50">
        <div className="spinner"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-purple-50 via-pink-50 to-orange-50 p-6">
        <div className="bg-white rounded-2xl shadow-xl p-10 max-w-md text-center" data-testid="public-journey-error">
          <Lock className="w-10 h-10 mx-auto text-gray-400 mb-3" />
          <h2 className="text-2xl font-bold text-gray-800 mb-2">Link Unavailable</h2>
          <p className="text-gray-600">{error}</p>
        </div>
      </div>
    );
  }

  if (!data) return null;

  const s = data.sections || {};
  const c = data.completion || {};
  const profile = s.entrepreneur_profile;
  const persona = s.customer_persona;
  const money = s.money_plan;
  const brand = s.brand_identity;
  const mk = s.marketing_channels;
  const team = s.team_strengths;
  const pitch = s.pitch;
  const studentName = data.student?.name || "Student";

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-orange-50">
      {/* Branded Header */}
      <header
        className="bg-gradient-to-r from-purple-600 via-pink-500 to-orange-400 text-white py-8 shadow-lg"
        data-testid="public-journey-header"
      >
        <div className="container mx-auto px-4 max-w-5xl">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <div className="text-xs uppercase tracking-widest opacity-90 mb-1">
                Mini Business Plan v1 · Lemonade Stand
              </div>
              <h1 className="text-3xl sm:text-4xl font-bold flex items-center gap-2" data-testid="public-journey-title">
                <Award className="w-8 h-8" />
                {s.business_name || "My Journey Map"}
              </h1>
              <div className="text-sm opacity-90 mt-1" data-testid="public-journey-author">
                By <strong>{studentName}</strong>
                {profile && (
                  <> · {profile.icon} {profile.name}</>
                )}
              </div>
              {data.share?.label && (
                <div className="text-sm opacity-80 mt-1" data-testid="public-journey-label">
                  {data.share.label}
                </div>
              )}
              {data.share?.expires_at && (
                <div
                  className="mt-3 inline-flex items-center gap-2 bg-white/15 backdrop-blur-sm border border-white/30 px-3 py-1.5 rounded-full text-sm font-semibold"
                  data-testid="public-journey-validity"
                >
                  ✅ Valid through{" "}
                  {new Date(data.share.expires_at).toLocaleDateString(undefined, {
                    year: "numeric", month: "long", day: "numeric",
                  })}
                </div>
              )}
            </div>
            <div className="flex flex-col items-end gap-2">
              <div className="text-right">
                <div className="text-3xl font-bold" data-testid="public-journey-percent">{c.percent}%</div>
                <div className="text-xs opacity-90">{c.completed_sessions} of {c.total_sessions} sessions</div>
              </div>
              <div className="text-xs opacity-80">Read-only · Token-protected</div>
            </div>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-8 max-w-5xl">
        {c.all_complete && (
          <div
            className="bg-emerald-50 border-2 border-emerald-200 rounded-xl p-3 mb-5 text-emerald-800 text-sm font-semibold flex items-center gap-2"
            data-testid="public-journey-complete-banner"
          >
            🎉 {studentName} has completed every session of the Middle School path!
          </div>
        )}

        {/* PDF download bar */}
        <div className="bg-white rounded-2xl shadow-md p-4 mb-6 flex items-center justify-between flex-wrap gap-3">
          <div className="text-sm text-gray-700">
            Take this Mini Business Plan offline as a one-page PDF — perfect for printing or emailing.
          </div>
          <button
            onClick={downloadPDF}
            disabled={downloading}
            className="flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-purple-600 to-pink-600 text-white font-semibold rounded-lg shadow-md hover:shadow-lg disabled:opacity-50"
            data-testid="public-journey-download-pdf-btn"
          >
            <Download className="w-4 h-4" />
            {downloading ? "Generating..." : "Download PDF"}
          </button>
        </div>

        {/* Top row: profile + identified problem */}
        <div className="grid sm:grid-cols-2 gap-3 mb-3">
          <Section icon={<Sparkles className="w-4 h-4" />} title="Entrepreneur Profile" complete={!!profile} testId="public-jm-profile">
            <div className="text-2xl">{profile?.icon} <span className="font-bold text-gray-900 text-base">{profile?.name}</span></div>
            <div className="text-xs text-gray-600 mt-1">⚡ {profile?.superpower}</div>
          </Section>
          <Section icon={<Lightbulb className="w-4 h-4" />} title="Identified Problem" complete={!!s.identified_problem} testId="public-jm-problem">
            <p className="text-sm">{s.identified_problem}</p>
          </Section>
        </div>

        <div className="grid sm:grid-cols-2 gap-3 mb-3">
          <Section icon={<Zap className="w-4 h-4" />} title="My Solution" complete={!!s.solution} testId="public-jm-solution">
            <p className="text-sm">{s.solution}</p>
          </Section>
          <Section icon={<User className="w-4 h-4" />} title="Customer Persona" complete={!!(persona && persona.name)} testId="public-jm-persona">
            <div className="space-y-1">
              <KV label="Name" value={persona?.name} />
              <KV label="Pain" value={persona?.pain} />
              <KV label="Dream solution" value={persona?.words} />
            </div>
          </Section>
        </div>
        <div className="grid sm:grid-cols-2 gap-3 mb-3">
          <Section icon={<Palette className="w-4 h-4" />} title="Brand Identity" complete={!!(brand && brand.brand_name)} testId="public-jm-brand">
            <div className="space-y-1">
              <KV label="Name" value={brand?.brand_name} />
              <KV label="Tagline" value={brand?.tagline} />
              <KV label="Colors" value={[brand?.primary_color, brand?.secondary_color].filter(Boolean).join(" / ")} />
              <KV label="Logo" value={brand?.logo_idea} />
              <KV label="Voice" value={brand?.voice_3words} />
            </div>
          </Section>
          <Section icon={<DollarSign className="w-4 h-4" />} title="Money Plan" complete={!!(money && (money.unit_cost || money.price))} testId="public-jm-money">
            <div className="space-y-1">
              <KV label="Cost / unit" value={money?.unit_cost ? `$${money.unit_cost}` : null} />
              <KV label="Price / unit" value={money?.price ? `$${money.price}` : null} />
              <KV label="Profit" value={money?.profit ? `$${money.profit}` : null} />
              <KV label="Weekly goal" value={money?.weekly_goal ? `$${money.weekly_goal}` : null} />
              <KV label="Splits" value={[money?.reinvest_pct && `Reinvest ${money.reinvest_pct}%`, money?.save_pct && `Save ${money.save_pct}%`, money?.spend_pct && `Spend ${money.spend_pct}%`].filter(Boolean).join(" · ")} />
            </div>
          </Section>
        </div>
        <div className="grid sm:grid-cols-2 gap-3 mb-3">
          <Section icon={<Megaphone className="w-4 h-4" />} title="Marketing Channels" complete={!!(mk && mk.channel_1)} testId="public-jm-marketing">
            <div className="space-y-1">
              <KV label="Main" value={mk?.channel_1} />
              <KV label="Backup" value={mk?.channel_2} />
              <KV label="Signature extra" value={mk?.signature_extra} />
              <KV label="Story hook" value={mk?.brand_story} />
            </div>
          </Section>
          <Section icon={<Users className="w-4 h-4" />} title="Team & Leadership" complete={!!(team && team.my_role)} testId="public-jm-team">
            <div className="space-y-1">
              <KV label="My role" value={team?.my_role} />
              <KV label="Teammate" value={team?.teammate_role} />
              <KV label="Mentor" value={team?.mentor} />
              <KV label="Workflow" value={team?.workflow_summary} />
            </div>
          </Section>
        </div>

        {/* Pitch — full width */}
        <div className="mb-3">
          <Section icon={<Mic className="w-4 h-4" />} title="Pitch Summary" complete={!!(pitch && pitch.full_pitch)} testId="public-jm-pitch">
            {pitch?.full_pitch && (
              <p className="italic mb-2 whitespace-pre-wrap">"{pitch.full_pitch}"</p>
            )}
            <div className="text-xs text-gray-600 space-x-3">
              {pitch?.launch_date && <span><strong>Launch:</strong> {pitch.launch_date}</span>}
              {pitch?.day1_goal && <span><strong>Day-1 goal:</strong> {pitch.day1_goal}</span>}
              {pitch?.next_30 && <span><strong>Next 30:</strong> {pitch.next_30}</span>}
            </div>
          </Section>
        </div>

        {/* Bottom row: pathways · skills · standards */}
        <div className="grid md:grid-cols-3 gap-3">
          <Section icon={<Briefcase className="w-4 h-4" />} title="Career Pathways" complete={!!(s.career_pathways && s.career_pathways.length > 0)} testId="public-jm-pathways">
            <ul className="space-y-1 text-sm">
              {(s.career_pathways || []).slice(0, 4).map((p) => (
                <li key={p.key}>
                  {p.icon} {p.name}{p.in_demand && " ⭐"}
                </li>
              ))}
            </ul>
          </Section>
          <Section icon={<Zap className="w-4 h-4" />} title="Top Skills" complete={!!(s.top_employability_skills && s.top_employability_skills.length > 0)} testId="public-jm-skills">
            <ul className="space-y-1 text-sm">
              {(s.top_employability_skills || []).map((sk) => (
                <li key={sk.id}>
                  <span className="font-semibold">{sk.title}</span>{" "}
                  <span className="text-xs text-gray-500">({sk.id})</span>
                </li>
              ))}
            </ul>
          </Section>
          <Section icon={<FileCheck className="w-4 h-4" />} title="Standards Alignment" complete={!!(s.standards_alignment?.nevada_cte?.length || s.standards_alignment?.cctc?.length)} testId="public-jm-standards">
            <div className="text-xs space-y-2">
              {s.standards_alignment?.nevada_cte?.length > 0 && (
                <div><span className="font-bold text-orange-700">Nevada CTE:</span>{" "}
                  {s.standards_alignment.nevada_cte.map((x) => x.split("—")[0].replace("Nevada CTE", "").trim()).join(" · ")}
                </div>
              )}
              {s.standards_alignment?.cctc?.length > 0 && (
                <div><span className="font-bold text-blue-700">CCTC:</span>{" "}
                  {s.standards_alignment.cctc.map((x) => x.split("—")[0].replace("CCTC", "").trim()).join(" · ")}
                </div>
              )}
              {s.standards_alignment?.academic_credit_subjects?.length > 0 && (
                <div><span className="font-bold text-indigo-700">Academic credit:</span>{" "}
                  {s.standards_alignment.academic_credit_subjects.join(", ")}
                </div>
              )}
            </div>
          </Section>
        </div>

        {/* Footer */}
        <footer className="text-center mt-10 pb-4 text-xs text-gray-400">
          <Lock className="w-3 h-3 inline mr-1" /> Token-protected · Read-only · Only first names shown — no email, last name, or account info.
          <br />
          Built with the <strong>Lemonade Stand Business Plan</strong> · Standards-aligned with Nevada CTE / Perkins V + CCTC.
        </footer>
      </main>
    </div>
  );
}

export default PublicJourneyMap;
